import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import JobCard from "../components/JobCard";
import FilterSidebar from "../components/opportunities/FilterSidebar";
import {
  Sparkles, Search, ChevronDown, SlidersHorizontal, X, Briefcase,
} from "lucide-react";

/* ---------- helpers ---------- */
function pickSalaryBucket(p) {
  const min = p.salary_min ?? null;
  const max = p.salary_max ?? null;
  const lpa = p.package_lpa ?? null;
  const val = lpa || max || min;
  if (val == null) return null;
  const num = Number(val);
  if (!num || isNaN(num)) return null;
  if (num < 3) return "0-3";
  if (num < 6) return "3-6";
  if (num < 10) return "6-10";
  if (num < 15) return "10-15";
  if (num < 25) return "15-25";
  return "25+";
}
const SALARY_LABELS = {
  "0-3":   "0 – 3 LPA",
  "3-6":   "3 – 6 LPA",
  "6-10":  "6 – 10 LPA",
  "10-15": "10 – 15 LPA",
  "15-25": "15 – 25 LPA",
  "25+":   "25 LPA +",
};

function pickExpBucket(p) {
  const lo = p.experience_min ?? null;
  const hi = p.experience_max ?? null;
  const val = lo != null ? lo : (hi != null ? hi : null);
  if (val == null) return null;
  const num = Number(val);
  if (isNaN(num)) return null;
  if (num === 0) return "fresher";
  if (num <= 2) return "0-2";
  if (num <= 5) return "2-5";
  if (num <= 10) return "5-10";
  return "10+";
}
const EXP_LABELS = {
  "fresher": "Fresher",
  "0-2":     "0 – 2 Yrs",
  "2-5":     "2 – 5 Yrs",
  "5-10":    "5 – 10 Yrs",
  "10+":     "10+ Yrs",
};

const WORK_MODE_LABELS = {
  "remote":   "Remote",
  "hybrid":   "Hybrid",
  "onsite":   "On-site",
};
function pickWorkMode(p) {
  if (p.remote) return "remote";
  if (p.work_mode === "hybrid" || (p.location || "").toLowerCase().includes("hybrid")) return "hybrid";
  return "onsite";
}

const TYPE_LABELS = {
  "internship": "Internship",
  "permanent":  "Full-time",
  "contract":   "Contract",
  "project":    "Project",
};

const SORT_OPTIONS = [
  { value: "newest",    label: "Newest first" },
  { value: "salary_hi", label: "Salary: high → low" },
  { value: "salary_lo", label: "Salary: low → high" },
];

/* ---------- normalize external → JobCard shape ---------- */
function normalizeExternal(j) {
  return {
    id: `ext-${j.source}-${j.source_id}`,
    _external: true,
    source_id: j.source_id,
    source: j.source,
    external_url: j.url,
    title: j.title,
    company_name: j.company,
    company_logo: null,
    location: j.location,
    remote: !!j.remote,
    sc_domain: j.sc_domain,
    sc_domain_name: j.sc_domain_name,
    type: null,
    created_at: j.posted_at,
    posted_at: j.posted_at,
    skills_required: [],
  };
}

/* ---------- Main ---------- */
export default function PostingsPublic() {
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState("newest");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Data
  const [internal, setInternal] = useState([]);
  const [external, setExternal] = useState([]);
  const [scDomains, setScDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    type: [], sc_domain: [], location: [], experience: [], salary: [], work_mode: [], source: [],
  });

  // Load domain catalogue once
  useEffect(() => {
    api.get("/governance/domains").then(r => setScDomains(r.data.domains || [])).catch(() => {});
  }, []);

  // Load both feeds together
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/postings", { params: { q: q || undefined } }).then(r => r.data).catch(() => []),
      api.get("/jobs/external", { params: { q: q || undefined, limit: 100 } }).then(r => r.data).catch(() => []),
    ]).then(([intl, ext]) => {
      if (cancelled) return;
      setInternal(intl || []);
      setExternal((ext || []).map(normalizeExternal));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [q]);

  // Merged feed
  const feed = useMemo(() => {
    const src = filters.source.length === 0 ? ["skillconnect", "external"] : filters.source;
    const rows = [];
    if (src.includes("skillconnect")) rows.push(...internal);
    if (src.includes("external"))     rows.push(...external);
    return rows;
  }, [internal, external, filters.source]);

  // Apply filters
  const filtered = useMemo(() => {
    return feed.filter((p) => {
      if (filters.type.length && !filters.type.includes(p.type)) return false;
      if (filters.sc_domain.length && !filters.sc_domain.includes(p.sc_domain)) return false;
      if (filters.experience.length) {
        const b = pickExpBucket(p);
        if (!b || !filters.experience.includes(b)) return false;
      }
      if (filters.salary.length) {
        const b = pickSalaryBucket(p);
        if (!b || !filters.salary.includes(b)) return false;
      }
      if (filters.work_mode.length && !filters.work_mode.includes(pickWorkMode(p))) return false;
      if (filters.location.length) {
        const loc = (p.location || "").toLowerCase();
        if (!filters.location.some((v) => loc.includes(v.toLowerCase()))) return false;
      }
      return true;
    });
  }, [feed, filters]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sort === "newest") {
      arr.sort((a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
    } else if (sort === "salary_hi") {
      const val = (p) => Number(p.package_lpa || p.salary_max || p.salary_min || 0) || 0;
      arr.sort((a, b) => val(b) - val(a));
    } else if (sort === "salary_lo") {
      const val = (p) => Number(p.package_lpa || p.salary_min || p.salary_max || Infinity);
      arr.sort((a, b) => val(a) - val(b));
    }
    return arr;
  }, [filtered, sort]);

  // Build sidebar sections dynamically from data
  const sections = useMemo(() => {
    const distinctLocations = new Map();
    feed.forEach((p) => {
      const raw = (p.location || "").split(",")[0].trim();
      if (raw && raw.length < 30) distinctLocations.set(raw, (distinctLocations.get(raw) || 0) + 1);
    });
    const topLocations = [...distinctLocations.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([label, count]) => ({ value: label, label, count }));

    return [
      { key: "type", label: "Job type", options: Object.entries(TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })) },
      { key: "sc_domain", label: "SC Domain", options: scDomains.map((d) => ({ value: d.id, label: d.name })) },
      { key: "experience", label: "Experience", options: Object.entries(EXP_LABELS).map(([v, l]) => ({ value: v, label: l })) },
      { key: "salary", label: "Salary", options: Object.entries(SALARY_LABELS).map(([v, l]) => ({ value: v, label: l })) },
      { key: "work_mode", label: "Work mode", options: Object.entries(WORK_MODE_LABELS).map(([v, l]) => ({ value: v, label: l })) },
      { key: "location", label: "Location", options: topLocations },
      { key: "source", label: "Source", options: [
        { value: "skillconnect", label: "SkillConnect verified" },
        { value: "external", label: "Aggregated (web)" },
      ] },
    ];
  }, [feed, scDomains]);

  const onFilter = useCallback((key, next) => {
    setFilters((prev) => ({ ...prev, [key]: next }));
  }, []);
  const onClearAll = useCallback(() => {
    setFilters({ type: [], sc_domain: [], location: [], experience: [], salary: [], work_mode: [], source: [] });
  }, []);

  const doSearch = () => setQ(qInput.trim());

  const activeFilterCount = Object.values(filters).reduce((n, v) => n + v.length, 0);

  return (
    <div className="min-h-screen bg-paper">
      <PublicNavBar />

      {/* Search band */}
      <section className="bg-white border-b border-rule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <span className="eyebrow" data-testid="opportunities-eyebrow">
            <Sparkles size={11} strokeWidth={2.5} className="inline mr-1 align-text-bottom" />
            Live opportunities
          </span>
          <h1 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-2" data-testid="opportunities-title">
            Find your next role
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1 max-w-2xl">
            Governance-filtered jobs from verified companies + aggregated feeds from across the web.
            {" "}All matched to India's <span className="font-semibold text-signal">10 CoE domains</span>.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-2 max-w-3xl">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              <input
                className="w-full pl-10 pr-3 py-2.5 rounded-md border border-rule bg-white text-sm focus:border-signal focus:ring-2 focus:ring-signal/20 transition outline-none"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Job title, company, or skill — e.g. Embedded engineer"
                data-testid="opportunities-search-input"
              />
            </div>
            <button
              type="button"
              onClick={doSearch}
              className="px-6 py-2.5 rounded-md text-sm font-semibold text-white shadow-sm hover:shadow-md transition whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
              data-testid="opportunities-search-btn"
            >
              Search
            </button>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar — desktop */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <FilterSidebar
                sections={sections}
                values={filters}
                onChange={onFilter}
                onClearAll={onClearAll}
                defaultOpen={["type", "sc_domain", "experience", "salary", "work_mode"]}
              />
            </div>
          </div>

          {/* Feed */}
          <div>
            {/* Result + sort bar */}
            <div className="flex items-center justify-between gap-3 mb-4" data-testid="results-toolbar">
              <div className="text-sm text-slate-600">
                <span className="font-bold text-slate-900" data-testid="results-count">{sorted.length}</span>
                {" "}{sorted.length === 1 ? "job" : "jobs"} found
                {q && <span className="text-muted"> for "{q}"</span>}
              </div>
              <div className="flex items-center gap-2">
                {/* Mobile filter trigger */}
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rule text-sm font-semibold hover:border-signal transition"
                  data-testid="mobile-filter-toggle"
                >
                  <SlidersHorizontal size={14} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="text-[10px] font-bold bg-signal text-white rounded-full px-1.5 min-w-[16px] text-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {/* Sort */}
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-1.5 rounded-md border border-rule bg-white text-sm font-medium hover:border-signal transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-signal/20"
                    data-testid="sort-select"
                    aria-label="Sort jobs"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Card list */}
            {loading ? (
              <SkeletonList />
            ) : sorted.length === 0 ? (
              <EmptyState onReset={onClearAll} />
            ) : (
              <div className="space-y-3" data-testid="opportunities-list">
                {sorted.map((p) => (
                  <JobCard
                    key={p.id}
                    p={p}
                    external={!!p._external}
                    testid={p._external ? `opp-ext-${p.source_id}` : `opp-${p.id}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <PublicFooter />

      {/* Mobile filter bottom sheet */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-filter-sheet">
          <button
            type="button"
            aria-label="Close filters"
            onClick={() => setShowMobileFilters(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <div
            className="absolute bottom-0 inset-x-0 max-h-[85vh] bg-white rounded-t-2xl overflow-hidden flex flex-col"
            style={{ animation: "slideInFromRight 240ms ease-out" }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-rule">
              <div className="font-semibold text-sm inline-flex items-center gap-2">
                <SlidersHorizontal size={15} className="text-signal" /> Filters
                {activeFilterCount > 0 && (
                  <span className="text-[11px] font-bold bg-signal text-white rounded-full px-1.5 min-w-[18px] text-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setShowMobileFilters(false)}
                data-testid="mobile-filter-close"
                className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-50"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <FilterSidebar
                sections={sections}
                values={filters}
                onChange={onFilter}
                onClearAll={onClearAll}
                defaultOpen={["type", "sc_domain", "experience"]}
              />
            </div>
            <div className="border-t border-rule p-3 flex gap-2">
              <button
                type="button"
                onClick={onClearAll}
                className="flex-1 text-sm font-semibold py-2.5 rounded-md border border-rule hover:bg-slate-50 transition"
                data-testid="mobile-filter-reset"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 text-sm font-semibold py-2.5 rounded-md text-white transition"
                style={{ background: "linear-gradient(135deg, #006CFF 0%, #0040A8 100%)" }}
                data-testid="mobile-filter-apply"
              >
                Show {sorted.length} job{sorted.length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Empty / skeleton ---------- */
function SkeletonList() {
  return (
    <div className="space-y-3" data-testid="opportunities-skeleton">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-white border border-rule rounded-xl p-4 sm:p-5 animate-pulse">
          <div className="flex gap-4">
            <div className="w-14 h-14 rounded-md bg-slate-200 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-slate-200 rounded w-3/4" />
              <div className="h-3 bg-slate-200 rounded w-1/3" />
              <div className="flex gap-4 mt-2">
                <div className="h-3 bg-slate-100 rounded w-16" />
                <div className="h-3 bg-slate-100 rounded w-16" />
                <div className="h-3 bg-slate-100 rounded w-16" />
              </div>
              <div className="flex gap-1.5 mt-1">
                <div className="h-4 bg-slate-100 rounded w-14" />
                <div className="h-4 bg-slate-100 rounded w-14" />
                <div className="h-4 bg-slate-100 rounded w-14" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="bg-white border border-rule rounded-xl p-10 text-center" data-testid="opportunities-empty">
      <Briefcase size={32} className="mx-auto text-muted opacity-50" />
      <div className="mt-3 font-display font-bold text-base">No matching opportunities</div>
      <p className="text-sm text-muted mt-1">Try widening your filters or clearing your search.</p>
      <button
        type="button"
        onClick={onReset}
        className="mt-4 inline-flex px-4 py-2 rounded-md text-sm font-semibold text-signal border border-signal/40 hover:bg-signal hover:text-white transition"
      >
        Reset filters
      </button>
    </div>
  );
}
